import {
  Badge,
  Box,
  Center,
  Grid,
  Group,
  LoadingOverlay,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { IconActivityHeartbeat } from "@tabler/icons-react";
import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart as RechartsLineChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from "recharts";

import { PatientHeader } from "./components/PatientHeader";
import { Printable } from "@components";
import {
  CONCUSSION_RISK_COLOR,
  ConcussionRiskLevel,
  resolveChartAccent,
  resolveEyeSeriesColor,
} from "@constants/resultColors";
import { TestType } from "types/testTypes";
import { calculateAgeForTest, displayPatientName } from "@utils/CommonUtil";
import { buildOKNSegmentTraces } from "@utils/oknChartData";
import { snapshotsToChartData } from "types/tests/smoothPursuitUtils";
import useTestList from "@hooks/useTestList";
import CircularPursuitGraph from "../testContainer/common/CircularPursuitGraph";
import {
  computeDValueTicks,
  formatUserInputD,
} from "../testContainer/result/customResultContainers/NPCTAxisHelpers";

export interface ConcussionOverviewTestIds {
  oknUuid?: string;
  npcUuid?: string;
  antiSaccadeUuid?: string;
  smoothPursuitUuid?: string;
}

interface ConcussionOverviewProps {
  testIds?: ConcussionOverviewTestIds;
}

type RiskLevel = ConcussionRiskLevel;

interface PatientData {
  name: string;
  age: number | null;
  testDate: string;
}

interface MetricItem {
  label: string;
  value: string;
  unit?: string;
}

interface CardSummary {
  risk: RiskLevel;
  metrics: MetricItem[];
  detail: string;
}

const RISK_RANK: Record<RiskLevel, number> = {
  expected: 0,
  borderline: 1,
  monitor: 2,
  unavailable: 0,
};

const SACCADE_THRESHOLD_DEG = 5;
const SACCADE_SEARCH_WINDOW_S = 1.5;
const CORRECTION_WINDOW_S = 0.5;

// ─── pure helpers ────────────────────────────────────────────────────────────

function firstFromList(raw: unknown): any | null {
  if (Array.isArray(raw)) return raw[0] ?? null;
  if (
    raw &&
    typeof raw === "object" &&
    "data" in raw &&
    Array.isArray((raw as { data: unknown[] }).data)
  ) {
    return (raw as { data: unknown[] }).data[0] ?? null;
  }
  return raw ?? null;
}

function formatDate(date: Date, locale: string): string {
  return date.toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" });
}

function numberOrDash(value: unknown, decimals = 0): string {
  return typeof value === "number" && Number.isFinite(value) ? value.toFixed(decimals) : "-";
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function downsample<T>(items: T[], maxPoints = 320): T[] {
  if (items.length <= maxPoints) return items;
  const step = Math.ceil(items.length / maxPoints);
  return items.filter((_, index) => index % step === 0);
}

function countValid(items: any[], field: string): number {
  return items.filter((item) => item?.[field] === true).length;
}

function mean(values: number[]): number | null {
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
}

function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
}

function formatRatio(value: number | null | undefined, decimals = 2): string {
  return isFiniteNumber(value) ? value.toFixed(decimals) : "-";
}

function formatGainPair(left?: number | null, right?: number | null, combined?: number | null) {
  if (isFiniteNumber(left) || isFiniteNumber(right)) {
    return `L ${formatRatio(left)} / R ${formatRatio(right)}`;
  }
  if (isFiniteNumber(combined)) return formatRatio(combined);
  return "-";
}

function averageDefined(values: Array<number | null | undefined>): number | null {
  const defined = values.filter(isFiniteNumber);
  return mean(defined);
}

function distanceCm(distanceM?: number | null): string {
  return isFiniteNumber(distanceM) && distanceM > 0 ? (distanceM * 100).toFixed(1) : "-";
}

function distanceDiopters(distanceM?: number | null): string {
  return isFiniteNumber(distanceM) && distanceM > 0 ? formatUserInputD(distanceM) : "-";
}

// ─── computed-data interfaces ─────────────────────────────────────────────────

interface DirectionSpeedGain {
  left: number | null;
  right: number | null;
  combined: number | null;
}

interface OknComputedData {
  risk: RiskLevel;
  gains: Array<{ direction: string; speed: number; gain: DirectionSpeedGain }>;
}

interface SmoothPursuitComputedData {
  risk: RiskLevel;
  hasSamples: boolean;
  averageValid: number | null;
  leftPursuitGain: number | null;
  rightPursuitGain: number | null;
  clockwiseGain: number | null;
  counterClockwiseGain: number | null;
}

interface NpcComputedData {
  risk: RiskLevel;
  approachDistance: number | null;
  recoveryDistance: number | null;
}

interface AntiSaccadeComputedData {
  risk: RiskLevel;
  correctCount: number;
  correctedErrors: number;
  uncorrectedErrors: number;
  errorRate: number | null;
  medianLatency: number | null;
  trialCount: number;
}

// ─── computation functions (no display strings) ───────────────────────────────

function getOknGainFor(test: any | null, direction: string, speed: number): DirectionSpeedGain {
  const modernSeries: any[] = Array.isArray(test?.results?.oknGain?.series)
    ? test.results.oknGain.series
    : [];
  const modernMatches = modernSeries.filter(
    (entry) =>
      (entry?.direction ?? "").toLowerCase() === direction.toLowerCase() &&
      Number(entry?.speedDps ?? entry?.speedDegPerSecond) === speed,
  );

  if (modernMatches.length) {
    const leftEntry = modernMatches.find((entry) => entry?.eye === "left");
    const rightEntry = modernMatches.find((entry) => entry?.eye === "right");
    return {
      left: leftEntry?.summary?.gainMedian ?? leftEntry?.summary?.gainMean ?? null,
      right: rightEntry?.summary?.gainMedian ?? rightEntry?.summary?.gainMean ?? null,
      combined: null,
    };
  }

  const legacySegments: any[] = Array.isArray(test?.results?.metrics?.segmentGains)
    ? test.results.metrics.segmentGains
    : [];
  const legacyMatches = legacySegments.filter(
    (segment) =>
      (segment?.direction ?? "").toLowerCase() === direction.toLowerCase() &&
      Number(segment?.speedDegPerSecond) === speed,
  );

  return {
    left: averageDefined(legacyMatches.map((segment) => segment?.leftGain)),
    right: averageDefined(legacyMatches.map((segment) => segment?.rightGain)),
    combined: averageDefined(legacyMatches.map((segment) => segment?.combinedGain)),
  };
}

function computeOknData(test: any | null): OknComputedData {
  const rawSamples: any[] = Array.isArray(test?.results?.rawSamples) ? test.results.rawSamples : [];
  const validCount = countValid(rawSamples, "isEyeTrackingValid");
  const validRate = rawSamples.length ? validCount / rawSamples.length : null;
  const risk: RiskLevel =
    validRate === null ? "unavailable" : validRate >= 0.8 ? "expected" : "borderline";

  const gains = [
    { direction: "LeftToRight", speed: 20 },
    { direction: "RightToLeft", speed: 20 },
    { direction: "LeftToRight", speed: 60 },
    { direction: "RightToLeft", speed: 60 },
  ].map(({ direction, speed }) => ({
    direction,
    speed,
    gain: getOknGainFor(test, direction, speed),
  }));

  return { risk, gains };
}

function computeSmoothPursuitData(test: any | null): SmoothPursuitComputedData {
  const snapshots: any[] = Array.isArray(test?.results?.snapshots) ? test.results.snapshots : [];
  const leftValid = countValid(snapshots, "leftGazeValid");
  const rightValid = countValid(snapshots, "rightGazeValid");
  const averageValid = snapshots.length ? (leftValid + rightValid) / (snapshots.length * 2) : null;
  const metrics = test?.results?.metrics ?? {};
  return {
    risk: snapshots.length > 0 ? "borderline" : "unavailable",
    hasSamples: snapshots.length > 0,
    averageValid,
    leftPursuitGain: metrics?.leftPursuitGain ?? null,
    rightPursuitGain: metrics?.rightPursuitGain ?? null,
    clockwiseGain: averageDefined([
      metrics?.clockwise?.leftPursuitGain,
      metrics?.clockwise?.rightPursuitGain,
    ]),
    counterClockwiseGain: averageDefined([
      metrics?.counterClockwise?.leftPursuitGain,
      metrics?.counterClockwise?.rightPursuitGain,
    ]),
  };
}

function computeNpcData(test: any | null): NpcComputedData {
  const snapshots: any[] = Array.isArray(test?.results?.snapshots) ? test.results.snapshots : [];
  const userInputs: any[] = Array.isArray(test?.results?.userInputs) ? test.results.userInputs : [];
  const approach = userInputs.find(
    (input) => input?.isApproaching === true && input?.isValid === true,
  );
  const recovery = userInputs.find(
    (input) => input?.isApproaching === false && input?.isValid === true,
  );
  return {
    risk: snapshots.length ? "expected" : "unavailable",
    approachDistance: approach?.distance ?? null,
    recoveryDistance: recovery?.distance ?? null,
  };
}

function combinedHorizontalGaze(sample: any): number | null {
  const left = sample?.leftGazeAngles?.x;
  const right = sample?.rightGazeAngles?.x;
  const hasLeft = isFiniteNumber(left);
  const hasRight = isFiniteNumber(right);
  if (hasLeft && hasRight) return (left + right) / 2;
  if (hasLeft) return left;
  if (hasRight) return right;
  return null;
}

function indexSamplesByTrial(samples: any[]) {
  const byTrial = new Map<number, any[]>();
  for (const sample of samples) {
    if (!isFiniteNumber(sample?.trialIndex)) continue;
    const list = byTrial.get(sample.trialIndex) ?? [];
    list.push(sample);
    byTrial.set(sample.trialIndex, list);
  }
  for (const list of byTrial.values()) {
    list.sort((a, b) => a.timeStamp - b.timeStamp);
  }
  return byTrial;
}

// Client-side fallback: the backend stores only an aggregate errorRate, not per-trial outcomes.
// This re-analysis derives corrected/uncorrected error counts so the overview can display them
// when the backend does not include a per-trial breakdown.
function getAntiSaccadeOutcome(trial: any, samplesByTrial: Map<number, any[]>) {
  if (trial?.correct === true) {
    return {
      outcome: "correct" as const,
      latencyMs: trial?.latencyMs ?? trial?.reactionTimeMs ?? null,
    };
  }

  const peripheralAngleDeg =
    trial?.peripheralAngleDeg ??
    trial?.peripheralHorizontalDegrees ??
    (trial?.peripheralSide === "right" ? 1 : -1);
  const peripheralSign = Math.sign(peripheralAngleDeg) || 1;
  const peripheralShownAt = trial?.peripheralShownAt;
  const trialSamples = samplesByTrial.get(trial?.trialIndex) ?? [];
  let firstSaccadeIndex = -1;
  let primaryAngleDeg = trial?.firstSaccadeAngleDeg ?? null;
  let latencyMs = trial?.latencyMs ?? trial?.reactionTimeMs ?? null;

  if (isFiniteNumber(peripheralShownAt) && trialSamples.length) {
    const deadline = peripheralShownAt + SACCADE_SEARCH_WINDOW_S;
    for (let index = 0; index < trialSamples.length; index += 1) {
      const sample = trialSamples[index];
      if (sample.timeStamp < peripheralShownAt) continue;
      if (sample.timeStamp > deadline) break;
      const gaze = combinedHorizontalGaze(sample);
      if (gaze == null || Math.abs(gaze) < SACCADE_THRESHOLD_DEG) continue;
      firstSaccadeIndex = index;
      if (primaryAngleDeg == null) primaryAngleDeg = gaze;
      if (latencyMs == null) latencyMs = Math.round((sample.timeStamp - peripheralShownAt) * 1000);
      break;
    }
  }

  if (primaryAngleDeg == null) {
    return { outcome: "invalid" as const, latencyMs };
  }

  const primaryWasCorrect = Math.sign(primaryAngleDeg) !== peripheralSign;
  if (primaryWasCorrect) {
    return { outcome: "correct" as const, latencyMs };
  }

  if (firstSaccadeIndex >= 0) {
    const correctionDeadline = trialSamples[firstSaccadeIndex].timeStamp + CORRECTION_WINDOW_S;
    for (let index = firstSaccadeIndex + 1; index < trialSamples.length; index += 1) {
      const sample = trialSamples[index];
      if (sample.timeStamp > correctionDeadline) break;
      const gaze = combinedHorizontalGaze(sample);
      if (gaze == null) continue;
      if (Math.sign(gaze) !== peripheralSign && Math.abs(gaze) >= SACCADE_THRESHOLD_DEG) {
        return { outcome: "error_corrected" as const, latencyMs };
      }
    }
  }

  return { outcome: "error_uncorrected" as const, latencyMs };
}

function computeAntiSaccadeData(test: any | null): AntiSaccadeComputedData {
  const trials: any[] = Array.isArray(test?.results?.trials) ? test.results.trials : [];
  const samples: any[] = Array.isArray(test?.results?.samples) ? test.results.samples : [];
  const samplesByTrial = indexSamplesByTrial(samples);
  const outcomes = trials.map((trial) => getAntiSaccadeOutcome(trial, samplesByTrial));
  const correctCount = outcomes.filter((o) => o.outcome === "correct").length;
  const correctedErrors = outcomes.filter((o) => o.outcome === "error_corrected").length;
  const uncorrectedErrors = outcomes.filter((o) => o.outcome === "error_uncorrected").length;
  const validTrials = outcomes.filter((o) => o.outcome !== "invalid").length;
  const computedErrorRate = validTrials
    ? (correctedErrors + uncorrectedErrors) / validTrials
    : null;
  // Prefer client-computed rate (always 0–1). Fall back to backend errorRate only when trials are
  // absent; the backend field is expected to be a 0–1 ratio, not a percentage.
  const errorRate =
    computedErrorRate ??
    (typeof test?.results?.errorRate === "number" ? test.results.errorRate : null);
  const medianLatency = median(
    outcomes
      .filter((o) => o.outcome === "correct" && isFiniteNumber(o.latencyMs))
      .map((o) => o.latencyMs),
  );
  const risk: RiskLevel =
    errorRate === null
      ? "unavailable"
      : errorRate >= 0.5
        ? "monitor"
        : errorRate >= 0.25
          ? "borderline"
          : "expected";

  return {
    risk,
    correctCount,
    correctedErrors,
    uncorrectedErrors,
    errorRate,
    medianLatency,
    trialCount: trials.length,
  };
}

// ─── UI components ────────────────────────────────────────────────────────────

function MetricGrid({ metrics, cols = 4 }: { metrics: MetricItem[]; cols?: 3 | 4 }) {
  return (
    <SimpleGrid cols={cols} spacing="sm">
      {metrics.map((metric) => (
        <Paper
          key={metric.label}
          withBorder
          radius="md"
          p="sm"
          bg="var(--oculera-surface-alt)"
          style={{ minHeight: 68 }}
        >
          <Text size="10px" c="dimmed" fw={800} tt="uppercase" lh={1.25}>
            {metric.label}
          </Text>
          <Text fw={800} size="lg" lh={1.1} mt={6}>
            {metric.value}
            {metric.unit && (
              <Text span size="xs" c="dimmed" fw={700}>
                {metric.unit}
              </Text>
            )}
          </Text>
        </Paper>
      ))}
    </SimpleGrid>
  );
}

function ChartFrame({
  title,
  rightLabel,
  children,
}: {
  title: string;
  rightLabel: string;
  children: ReactNode;
}) {
  return (
    <Paper withBorder radius="md" p="sm" bg="var(--mantine-color-body)">
      <Group justify="space-between" gap="sm" mb="xs">
        <Text size="xs" c="dimmed" fw={700} tt="uppercase">
          {title}
        </Text>
        <Text size="xs" c="dimmed" fw={700} tt="uppercase">
          {rightLabel}
        </Text>
      </Group>
      {children}
    </Paper>
  );
}

function ScreeningCard({
  kicker,
  title,
  badge,
  description,
  chart,
  metrics,
  detail,
}: {
  kicker: string;
  title: string;
  badge: RiskLevel;
  description: string;
  chart?: ReactNode;
  metrics: MetricItem[];
  detail: string;
}) {
  const { t } = useTranslation();
  const ns = "extraTests.concussionOverview";
  return (
    <Paper withBorder radius="md" p="md" h="100%" className="print-no-break">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={3}>
            <Text size="xs" c="dimmed" fw={800} tt="uppercase">
              {kicker}
            </Text>
            <Title order={5}>{title}</Title>
          </Stack>
          <Badge color={CONCUSSION_RISK_COLOR[badge]} variant="filled" size="lg">
            {t(`${ns}.riskLabel.${badge}`)}
          </Badge>
        </Group>
        <Text size="sm" c="dimmed">
          {description}
        </Text>
        {chart}
        <MetricGrid metrics={metrics} cols={metrics.length <= 3 ? 3 : 4} />
        <Paper withBorder radius="md" p="sm" bg="var(--mantine-color-blue-0)">
          <Text size="sm" c="blue.9">
            <Text span fw={700}>
              {t(`${ns}.whatThisMeans`)}{" "}
            </Text>
            {detail}
          </Text>
        </Paper>
      </Stack>
    </Paper>
  );
}

interface LineSeriesConfig {
  dataKey: string;
  name: string;
  color: string;
}

interface ReferenceMarker {
  x: number;
  label: string;
  color: string;
}

function hasSeriesData(data: any[], dataKey: string): boolean {
  return data.some((point) => isFiniteNumber(point?.[dataKey]));
}

function visibleSeries(data: any[], series: LineSeriesConfig[]) {
  return series.filter((item) => hasSeriesData(data, item.dataKey));
}

function EmptyChartMessage({ message }: { message: string }) {
  return (
    <Center h={220}>
      <Text size="sm" c="dimmed" ta="center">
        {message}
      </Text>
    </Center>
  );
}

function CompactLineChart({
  data,
  series,
  referenceMarkers = [],
  xKey = "time",
  yDomain,
  height = 240,
}: {
  data: any[];
  series: LineSeriesConfig[];
  referenceMarkers?: ReferenceMarker[];
  xKey?: string;
  yDomain?: [number, number];
  height?: number;
}) {
  const { t } = useTranslation();
  const ns = "extraTests.concussionOverview";
  const theme = useMantineTheme();
  const gridColor = resolveChartAccent(theme, "grid");
  const tickColor = resolveChartAccent(theme, "tick");
  const activeSeries = visibleSeries(data, series);

  if (data.length === 0 || activeSeries.length === 0) {
    return <EmptyChartMessage message={t(`${ns}.chart.noSamples`)} />;
  }

  return (
    <Box h={height}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsLineChart data={data} margin={{ top: 8, right: 12, bottom: 8, left: -12 }}>
          <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
          <XAxis
            dataKey={xKey}
            type="number"
            domain={["dataMin", "dataMax"]}
            tickFormatter={(value) => Number(value).toFixed(0)}
            tick={{ fontSize: 11, fill: tickColor }}
          />
          <YAxis
            domain={yDomain ?? ["auto", "auto"]}
            tickFormatter={(value) => Number(value).toFixed(0)}
            tick={{ fontSize: 11, fill: tickColor }}
          />
          <Tooltip
            formatter={(value, name) => [isFiniteNumber(value) ? value.toFixed(2) : value, name]}
            labelFormatter={(label) =>
              xKey === "trial" ? `Trial ${label}` : `${Number(label).toFixed(2)} s`
            }
          />
          <Legend verticalAlign="top" height={30} wrapperStyle={{ fontSize: 12 }} />
          {referenceMarkers.map((marker) => (
            <ReferenceLine
              key={`${marker.label}-${marker.x}`}
              x={marker.x}
              stroke={marker.color}
              strokeDasharray="5 4"
              ifOverflow="extendDomain"
              label={{
                value: marker.label,
                position: "insideTopRight",
                fontSize: 11,
                fill: marker.color,
              }}
            />
          ))}
          {activeSeries.map((item) => (
            <Line
              key={item.dataKey}
              type="monotone"
              dataKey={item.dataKey}
              name={item.name}
              stroke={item.color}
              strokeWidth={2}
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />
          ))}
        </RechartsLineChart>
      </ResponsiveContainer>
    </Box>
  );
}

const OKN_TRACE_TARGETS = [
  { direction: "LeftToRight", speed: 20 },
  { direction: "RightToLeft", speed: 20 },
  { direction: "LeftToRight", speed: 60 },
  { direction: "RightToLeft", speed: 60 },
];

function OknTraceGrid({ test }: { test: any | null }) {
  const { t } = useTranslation();
  const ns = "extraTests.concussionOverview";
  const theme = useMantineTheme();
  const leftEyeColor = resolveEyeSeriesColor(theme, "left", 8);
  const rightEyeColor = resolveEyeSeriesColor(theme, "right", 8);
  const rawSamples: any[] = Array.isArray(test?.results?.rawSamples) ? test.results.rawSamples : [];
  const traces = useMemo(() => buildOKNSegmentTraces(rawSamples), [rawSamples]);

  function tDirection(dir: string): string {
    if (dir.toLowerCase() === "lefttoright") return t(`${ns}.direction.leftToRight`);
    if (dir.toLowerCase() === "righttoleft") return t(`${ns}.direction.rightToLeft`);
    return dir;
  }

  return (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
      {OKN_TRACE_TARGETS.map((target) => {
        const trace = traces.find(
          (candidate) =>
            candidate.horizontal &&
            candidate.speedDegPerSecond === target.speed &&
            candidate.direction === target.direction,
        );
        const data =
          trace?.points.map((point) => ({
            time: point.t,
            left: point.left,
            right: point.right,
          })) ?? [];

        return (
          <Paper key={`${target.direction}-${target.speed}`} withBorder radius="md" p="sm">
            <Text size="xs" fw={800} tt="uppercase" c="dimmed" mb={4}>
              {t(`${ns}.chart.speedLabel`, {
                direction: tDirection(target.direction),
                speed: target.speed,
              })}
            </Text>
            <CompactLineChart
              data={data}
              height={180}
              series={[
                { dataKey: "left", name: t(`${ns}.chart.leftEye`), color: leftEyeColor },
                { dataKey: "right", name: t(`${ns}.chart.rightEye`), color: rightEyeColor },
              ]}
            />
          </Paper>
        );
      })}
    </SimpleGrid>
  );
}

function SmoothPursuitChart({ test }: { test: any | null }) {
  const { t } = useTranslation();
  const ns = "extraTests.concussionOverview";
  const snapshots: any[] = Array.isArray(test?.results?.snapshots) ? test.results.snapshots : [];
  const chartData = useMemo(() => snapshotsToChartData(snapshots), [snapshots]);

  if (chartData.length === 0) {
    return <EmptyChartMessage message={t(`${ns}.chart.noPursuitSnapshots`)} />;
  }

  return (
    <Box maw={260} mx="auto">
      <CircularPursuitGraph data={chartData} compact height={240} showPaper={false} />
    </Box>
  );
}

function getNpcPlotX(snapshot: any, side: "left" | "right"): number | null {
  if (side === "left" && isFiniteNumber(snapshot?.leftHorizGazeAngleDeg)) {
    return snapshot.leftHorizGazeAngleDeg;
  }
  if (side === "right" && isFiniteNumber(snapshot?.rightHorizGazeAngleDeg)) {
    return 80 - snapshot.rightHorizGazeAngleDeg;
  }

  const rawAngle = side === "left" ? snapshot?.leftGazeAngles?.x : snapshot?.rightGazeAngles?.x;
  if (!isFiniteNumber(rawAngle)) return null;
  return side === "left" ? 40 + rawAngle : 40 - rawAngle;
}

function NpcFullConvergenceChart({ test }: { test: any | null }) {
  const { t } = useTranslation();
  const ns = "extraTests.concussionOverview";
  const theme = useMantineTheme();
  const leftEyeColor = resolveEyeSeriesColor(theme, "left", 8);
  const rightEyeColor = resolveEyeSeriesColor(theme, "right", 8);
  const expectedColor = resolveChartAccent(theme, "expected");
  const breakColor = resolveChartAccent(theme, "npcBreak");
  const recoveryColor = resolveChartAccent(theme, "npcRecovery");
  const gridColor = resolveChartAccent(theme, "grid");
  const tickColor = resolveChartAccent(theme, "tick");

  const snapshots: any[] = Array.isArray(test?.results?.snapshots) ? test.results.snapshots : [];
  const userInputs: any[] = Array.isArray(test?.results?.userInputs) ? test.results.userInputs : [];
  const sampled = useMemo(() => downsample(snapshots, 280), [snapshots]);
  const leftData = sampled
    .filter((snapshot) => snapshot?.leftGazeValid === true)
    .map((snapshot) => ({ x: getNpcPlotX(snapshot, "left"), y: snapshot.timestamp }))
    .filter((point) => point.x != null);
  const rightData = sampled
    .filter((snapshot) => snapshot?.rightGazeValid === true)
    .map((snapshot) => ({ x: getNpcPlotX(snapshot, "right"), y: snapshot.timestamp }))
    .filter((point) => point.x != null);
  const expectedLeftData = sampled
    .filter((snapshot) => isFiniteNumber(snapshot?.expectedConvergenceAngleDeg))
    .map((snapshot) => ({ x: 40 + snapshot.expectedConvergenceAngleDeg, y: snapshot.timestamp }));
  const expectedRightData = sampled
    .filter((snapshot) => isFiniteNumber(snapshot?.expectedConvergenceAngleDeg))
    .map((snapshot) => ({ x: 40 - snapshot.expectedConvergenceAngleDeg, y: snapshot.timestamp }));
  const { ticks, labels } = useMemo(() => computeDValueTicks(snapshots as any), [snapshots]);
  const referenceLines = userInputs
    .filter((input) => input?.isValid === true && isFiniteNumber(input?.timestamp))
    .map((input) => ({
      y: input.timestamp,
      label: t(`${ns}.${input.isApproaching ? "npc.breakLabel" : "npc.recoveryLabel"}`, {
        distance: distanceDiopters(input.distance),
      }),
      isApproaching: input.isApproaching as boolean,
      color: input.isApproaching ? breakColor : recoveryColor,
    }));

  if (leftData.length === 0 && rightData.length === 0) {
    return <EmptyChartMessage message={t(`${ns}.chart.noConvergenceSamples`)} />;
  }

  return (
    <Box h={540}>
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 16, right: 42, bottom: 42, left: 46 }}>
          <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
          <XAxis
            dataKey="x"
            type="number"
            domain={[0, 80]}
            ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80]}
            tickFormatter={(value: number) => `${value <= 40 ? value : 80 - value}`}
            tick={{ fontSize: 11, fill: tickColor }}
          >
            <Label value={t(`${ns}.chart.gazeAngleDeg`)} position="insideBottom" offset={-26} />
          </XAxis>
          <YAxis
            dataKey="y"
            type="number"
            reversed
            ticks={ticks}
            interval={0}
            tickFormatter={(value: number) => labels.get(value) ?? ""}
            tick={{ fontSize: 11, fill: tickColor }}
          >
            <Label
              value={t(`${ns}.chart.inverseDistanceD`)}
              angle={-90}
              position="insideLeft"
              offset={4}
            />
          </YAxis>
          <ZAxis range={[0, 0]} />
          <Tooltip
            formatter={(value, name) => [isFiniteNumber(value) ? value.toFixed(2) : value, name]}
            labelFormatter={(_, payload) =>
              payload?.[0]?.payload?.y != null ? `${Number(payload[0].payload.y).toFixed(2)} s` : ""
            }
          />
          <Legend verticalAlign="top" height={28} wrapperStyle={{ fontSize: 12 }} />
          {referenceLines.map((line) => (
            <ReferenceLine
              key={`${line.label}-${line.y}`}
              y={line.y}
              stroke={line.color}
              strokeDasharray="5 4"
              ifOverflow="extendDomain"
              label={{
                value: line.label,
                position: line.isApproaching ? "insideTopRight" : "insideBottomRight",
                fontSize: 11,
                fill: line.color,
              }}
            />
          ))}
          <Scatter
            name={t(`${ns}.chart.leftEyeGaze`)}
            data={leftData}
            fill={leftEyeColor}
            line={{ stroke: leftEyeColor, strokeWidth: 1.5 }}
            shape={() => null}
            legendType="none"
          />
          <Scatter
            name={t(`${ns}.chart.rightEyeGaze`)}
            data={rightData}
            fill={rightEyeColor}
            line={{ stroke: rightEyeColor, strokeWidth: 1.5 }}
            shape={() => null}
            legendType="none"
          />
          <Scatter
            name={t(`${ns}.chart.expectedAngle`)}
            data={expectedLeftData}
            fill="none"
            line={{ stroke: expectedColor, strokeWidth: 1, strokeDasharray: "5 4" }}
            shape={() => null}
            legendType="none"
          />
          <Scatter
            data={expectedRightData}
            fill="none"
            line={{ stroke: expectedColor, strokeWidth: 1, strokeDasharray: "5 4" }}
            shape={() => null}
            legendType="none"
          />
        </ScatterChart>
      </ResponsiveContainer>
    </Box>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export default function ConcussionOverview({ testIds }: ConcussionOverviewProps = {}) {
  const { t, i18n } = useTranslation();
  const ns = "extraTests.concussionOverview";

  function tDirection(dir: string): string {
    if (dir.toLowerCase() === "lefttoright") return t(`${ns}.direction.leftToRight`);
    if (dir.toLowerCase() === "righttoleft") return t(`${ns}.direction.rightToLeft`);
    return dir;
  }

  const { testList: oknList, testListLoading: oknLoading } = useTestList(
    testIds?.oknUuid ? { uuid: testIds.oknUuid } : {},
    { skip: !testIds?.oknUuid },
  );
  const { testList: npcList, testListLoading: npcLoading } = useTestList(
    testIds?.npcUuid ? { uuid: testIds.npcUuid } : {},
    { skip: !testIds?.npcUuid },
  );
  const { testList: antiList, testListLoading: antiLoading } = useTestList(
    testIds?.antiSaccadeUuid ? { uuid: testIds.antiSaccadeUuid } : {},
    { skip: !testIds?.antiSaccadeUuid },
  );
  const { testList: smoothList, testListLoading: smoothLoading } = useTestList(
    testIds?.smoothPursuitUuid ? { uuid: testIds.smoothPursuitUuid } : {},
    { skip: !testIds?.smoothPursuitUuid },
  );

  const oknTest = firstFromList(oknList);
  const npcTest = firstFromList(npcList);
  const antiTest = firstFromList(antiList);
  const smoothTest = firstFromList(smoothList);

  const patientData = useMemo((): PatientData => {
    const fallback = t(`${ns}.patientFallback`);
    const locale = i18n.language;
    const source = [oknTest, npcTest, antiTest, smoothTest].find(
      (test) => test?.startDate || test?.patient,
    );
    if (!source) return { name: fallback, age: null, testDate: formatDate(new Date(), locale) };
    const startDate = source.startDate ? new Date(source.startDate) : new Date();
    return {
      name: source.patient?.name ? displayPatientName(source.patient.name) : fallback,
      age: source.patient?.dob
        ? calculateAgeForTest(startDate, new Date(source.patient.dob))
        : null,
      testDate: formatDate(startDate, locale),
    };
  }, [oknTest, npcTest, antiTest, smoothTest, t, i18n.language]);

  const oknSummary = useMemo((): CardSummary => {
    const data = computeOknData(oknTest);
    return {
      risk: data.risk,
      detail: t(`${ns}.okn.detail.${data.risk === "expected" ? "expected" : "other"}`),
      metrics: data.gains.map(({ direction, speed, gain }) => ({
        label: t(`${ns}.okn.metrics.gainLabel`, { direction: tDirection(direction), speed }),
        value: formatGainPair(gain.left, gain.right, gain.combined),
      })),
    };
  }, [oknTest, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const npcSummary = useMemo((): CardSummary => {
    const data = computeNpcData(npcTest);
    return {
      risk: data.risk,
      detail: t(`${ns}.npc.detail`),
      metrics: [
        {
          label: t(`${ns}.npc.metrics.breakPoint`),
          value: distanceCm(data.approachDistance),
          unit: data.approachDistance
            ? t(`${ns}.npc.metrics.distanceUnit`, {
                diopters: distanceDiopters(data.approachDistance),
              })
            : undefined,
        },
        {
          label: t(`${ns}.npc.metrics.recoveryPoint`),
          value: distanceCm(data.recoveryDistance),
          unit: data.recoveryDistance
            ? t(`${ns}.npc.metrics.distanceUnit`, {
                diopters: distanceDiopters(data.recoveryDistance),
              })
            : undefined,
        },
      ],
    };
  }, [npcTest, t]);

  const smoothSummary = useMemo((): CardSummary => {
    const data = computeSmoothPursuitData(smoothTest);
    return {
      risk: data.risk,
      detail: t(`${ns}.smoothPursuit.detail.${data.hasSamples ? "hasSamples" : "noSamples"}`),
      metrics: [
        {
          label: t(`${ns}.smoothPursuit.metrics.validSamples`),
          value: data.averageValid === null ? "-" : (data.averageValid * 100).toFixed(1),
          unit: "%",
        },
        {
          label: t(`${ns}.smoothPursuit.metrics.leftGain`),
          value: formatRatio(data.leftPursuitGain),
        },
        {
          label: t(`${ns}.smoothPursuit.metrics.rightGain`),
          value: formatRatio(data.rightPursuitGain),
        },
        {
          label: t(`${ns}.smoothPursuit.metrics.clockwiseGain`),
          value: formatRatio(data.clockwiseGain),
        },
        {
          label: t(`${ns}.smoothPursuit.metrics.counterClockwiseGain`),
          value: formatRatio(data.counterClockwiseGain),
        },
      ],
    };
  }, [smoothTest, t]);

  const antiSummary = useMemo((): CardSummary => {
    const data = computeAntiSaccadeData(antiTest);
    return {
      risk: data.risk,
      detail: t(`${ns}.antiSaccade.detail.${data.risk === "monitor" ? "monitor" : "other"}`),
      metrics: [
        {
          label: t(`${ns}.antiSaccade.metrics.correctAntiSaccades`),
          value: String(data.correctCount),
        },
        {
          label: t(`${ns}.antiSaccade.metrics.correctedErrors`),
          value: String(data.correctedErrors),
        },
        {
          label: t(`${ns}.antiSaccade.metrics.uncorrectedErrors`),
          value: String(data.uncorrectedErrors),
        },
        {
          label: t(`${ns}.antiSaccade.metrics.medianLatency`),
          value: numberOrDash(data.medianLatency),
          unit: " ms",
        },
        {
          label: t(`${ns}.antiSaccade.metrics.incorrectFirstGlance`),
          value: data.errorRate === null ? "-" : (data.errorRate * 100).toFixed(1),
          unit: "%",
        },
        {
          label: t(`${ns}.antiSaccade.metrics.trials`),
          value: data.trialCount ? String(data.trialCount) : "-",
        },
      ],
    };
  }, [antiTest, t]);

  const summaries = [oknSummary, smoothSummary, npcSummary, antiSummary];
  const overallRisk = summaries.reduce<RiskLevel>(
    (highest, summary) => (RISK_RANK[summary.risk] > RISK_RANK[highest] ? summary.risk : highest),
    "expected",
  );
  const loading = oknLoading || npcLoading || antiLoading || smoothLoading;

  // Each Printable.Section is one A4 print page (breakInside: avoid + breakAfter: page),
  // so the content is split to fit: header + summary + OKN on page 1, the rest on page 2.
  return (
    <>
      <Printable.Section>
        <Box pos="relative">
          <LoadingOverlay
            visible={loading}
            zIndex={1000}
            overlayProps={{ radius: "sm", blur: 2 }}
          />
          <PatientHeader
            patientData={patientData}
            icon={<IconActivityHeartbeat color="white" size={24} />}
            title={t(`${ns}.title`)}
            subtitle={t(`${ns}.subtitle`)}
            overallStatus={{
              label: t(`${ns}.overallStatus`, { status: t(`${ns}.riskLabel.${overallRisk}`) }),
              color: CONCUSSION_RISK_COLOR[overallRisk],
            }}
          />

          <Stack gap="lg">
            <Paper withBorder radius="md" p="lg" bg="var(--oculera-surface-alt)">
              <Stack gap="md">
                <Group align="flex-start" justify="space-between">
                  <Stack gap={6} style={{ maxWidth: 790 }}>
                    <Title order={4}>{t(`${ns}.summary.title`)}</Title>
                    <Text size="sm" c="dimmed">
                      {t(`${ns}.summary.description`)}
                    </Text>
                  </Stack>
                  <ThemeIcon
                    size="lg"
                    radius="xl"
                    color={CONCUSSION_RISK_COLOR[overallRisk]}
                    variant="light"
                  >
                    <IconActivityHeartbeat size={22} />
                  </ThemeIcon>
                </Group>
                <SimpleGrid cols={4} spacing="sm">
                  {(
                    [
                      ["summary.okn", "summary.okn"],
                      ["summary.smoothPursuit", "summary.smoothPursuit"],
                      ["summary.npc", "summary.npc"],
                      ["summary.antiSaccade", "summary.antiSaccade"],
                    ] as const
                  ).map(([labelKey, descKey]) => (
                    <Paper
                      key={labelKey}
                      withBorder
                      radius="md"
                      p="sm"
                      bg="var(--mantine-color-body)"
                    >
                      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                        {t(`${ns}.${labelKey}.label`)}
                      </Text>
                      <Text size="sm" fw={700}>
                        {t(`${ns}.${descKey}.description`)}
                      </Text>
                    </Paper>
                  ))}
                </SimpleGrid>
              </Stack>
            </Paper>

            <ScreeningCard
              kicker={t(`${ns}.okn.kicker`)}
              title={t(`${ns}.okn.title`)}
              badge={oknSummary.risk}
              description={t(`${ns}.okn.description`)}
              chart={
                <ChartFrame
                  title={t(`${ns}.okn.chartTitle`)}
                  rightLabel={t(`${ns}.okn.chartRightLabel`)}
                >
                  <OknTraceGrid test={oknTest} />
                </ChartFrame>
              }
              metrics={oknSummary.metrics}
              detail={oknSummary.detail}
            />
          </Stack>
        </Box>
      </Printable.Section>

      <Printable.Section>
        <Stack gap="lg">
          <Grid gutter="md" align="stretch" className="print-row-2">
            <Grid.Col span={{ base: 12, md: 6 }}>
              <ScreeningCard
                kicker={t(`${ns}.npc.kicker`)}
                title={t(`${ns}.npc.title`)}
                badge={npcSummary.risk}
                description={t(`${ns}.npc.description`)}
                chart={
                  <ChartFrame
                    title={t(`${ns}.npc.chartTitle`)}
                    rightLabel={t(`${ns}.npc.chartRightLabel`)}
                  >
                    <NpcFullConvergenceChart test={npcTest} />
                  </ChartFrame>
                }
                metrics={npcSummary.metrics}
                detail={npcSummary.detail}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <ScreeningCard
                kicker={t(`${ns}.smoothPursuit.kicker`)}
                title={t(`${ns}.smoothPursuit.title`)}
                badge={smoothSummary.risk}
                description={t(`${ns}.smoothPursuit.description`)}
                chart={
                  <ChartFrame
                    title={t(`${ns}.smoothPursuit.chartTitle`)}
                    rightLabel={t(`${ns}.smoothPursuit.chartRightLabel`)}
                  >
                    <SmoothPursuitChart test={smoothTest} />
                  </ChartFrame>
                }
                metrics={smoothSummary.metrics}
                detail={smoothSummary.detail}
              />
            </Grid.Col>
          </Grid>

          <ScreeningCard
            kicker={t(`${ns}.antiSaccade.kicker`)}
            title={t(`${ns}.antiSaccade.title`)}
            badge={antiSummary.risk}
            description={t(`${ns}.antiSaccade.description`)}
            metrics={antiSummary.metrics}
            detail={antiSummary.detail}
          />

          <Text size="xs" c="dimmed" ta="center">
            {t(`${ns}.disclaimer`)}
          </Text>
        </Stack>
      </Printable.Section>
    </>
  );
}

export function isConcussionSuiteTestType(testType?: string | null): boolean {
  return (
    testType === TestType.OptokineticNystagmus ||
    testType === TestType.NearPointConvergence ||
    testType === TestType.AntiSaccades ||
    testType === TestType.SmoothPursuit
  );
}
